/**
 * Thin fetch wrapper.
 * - Always sends credentials: 'include' (session cookie).
 * - On 401, fires an 'auth-expired' window event so the SPA can redirect.
 * - Throws ApiError with parsed JSON body on non-2xx.
 */

import type { Role } from '../auth/capabilities'

export class ApiError extends Error {
  status: number
  code: string

  constructor(status: number, code: string, message?: string) {
    super(message ?? code)
    this.status = status
    this.code = code
  }
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth-expired'))
  }

  let data: any = null
  try {
    data = await res.json()
  } catch {
    // not JSON — leave null
  }

  if (!res.ok) {
    // Project envelope → {error: {code, message}}
    const code = data?.error?.code ?? data?.detail ?? 'UNKNOWN'
    const message = data?.error?.message ?? undefined
    throw new ApiError(res.status, String(code), message)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body),
  delete: <T>(path: string) => call<T>('DELETE', path),
}

export interface AuthUser {
  id: number
  email: string
  role: Role
}
